import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type { Hoid } from "./data";

import { Button as ButtonV2 } from "../../Button";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { CrossIcon } from "../../Icons";
import { ResponsiveModal } from "../../ResponsiveModal";
import { showErrorToast } from "../../Toast";
import { Typo } from "../../Typo";
import { DiscardConfirmOverlay } from "./CreateProgramWizard/DiscardConfirmOverlay";
import { WizardFooter } from "./CreateProgramWizard/WizardFooter";
import { splitLabel } from "./data";
import { StepUpCancelledError } from "./StepUpCancelledError";
import { StepUpError, StepUpRedirect, StepUpVerifying } from "./StepUpFlow";
import { useStepUpFlowTranslations } from "./useStepUpFlowTranslations";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type ArchiveProgramModalProps = {
  /** The program being archived; `null` keeps the modal closed. */
  hoid: Hoid | null;
  onClose: () => void;
  /** Rejects on API failure — the modal stays open so the user can retry. */
  onArchived: (
    hoid: Hoid,
    options?: { signal?: AbortSignal; onRedirect?: () => void },
  ) => Promise<void>;
  isMobile?: boolean;
  showStepUp?: boolean;
  /**
   * Opened after a completed OTP: start on "Closing your program..." and run
   * `onResumeArchived` instead of waiting for the user to confirm.
   */
  resume?: boolean;
  /** Replays the interrupted archive; rejects on failure (→ error screen). */
  onResumeArchived?: (
    hoid: Hoid,
    options?: { signal?: AbortSignal },
  ) => Promise<void>;
};

type VerifyPhase = "redirect" | "verifying" | "error";

/**
 * Archive confirmation for a single rebate program, per the canvas's
 * `ArchiveProgramModal.tsx` (spec: `Archive-Rebate-Program-Functional-
 * Requirements.md`). Archiving used to commit straight from the kebab with no
 * confirmation at all.
 *
 * A single confirm screen, because both entry points in this build target one
 * specific program: the row kebab's "Close rebate program" and the Program
 * details drawer's archive button. The canvas's multi-select *picker* only opens
 * from the Sub IBs tab's parent-row kebab when a Sub IB has 2+ active programs;
 * that entry still routes to a "Coming soon" toast, so the picker is
 * deliberately not built here.
 *
 */
export const ArchiveProgramModal = ({
  hoid,
  onClose,
  onArchived,
  isMobile = false,
  showStepUp = false,
  resume = false,
  onResumeArchived,
}: ArchiveProgramModalProps) => {
  const { t } = useTranslation();
  const [committing, setCommitting] = useState(false);
  const [step, setStep] = useState<"confirm" | "verify">("confirm");
  const [verifyPhase, setVerifyPhase] = useState<VerifyPhase>("redirect");
  const [discardOpen, setDiscardOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // A successful archive deliberately leaves `committing` true — the parent
  // closes the modal by clearing `hoid`, so there is nothing to settle back
  // into. But `if (!hoid) return null` below hides this component without
  // unmounting it, so that flag survives the close: without this reset the next
  // open renders straight into the loading state with its CTA disabled.
  const resumedFor = useRef<string | null>(null);
  const submitAbort = useRef<AbortController | null>(null);
  const runId = useRef(0);
  const [inFlight, setInFlight] = useState(false);

  const showArchiveError = useCallback(
    () =>
      showErrorToast({
        title: t("subIBReport_archiveError", "Couldn't close rebate program"),
        description: t(
          "subIBReport_archiveErrorDescription",
          "Please try again.",
        ),
      }),
    [t],
  );

  useEffect(() => {
    if (hoid && resumedFor.current !== hoid.id) {
      // oxlint-disable-next-line react/set-state-in-effect
      setCommitting(false);
      // oxlint-disable-next-line react/set-state-in-effect
      setStep("confirm");
      setDiscardOpen(false);
    }
  }, [hoid]);

  useEffect(() => {
    if (!hoid) {
      resumedFor.current = null;
      return;
    }
    if (!resume || !onResumeArchived || resumedFor.current === hoid.id) return;
    resumedFor.current = hoid.id;
    const run = ++runId.current;
    setVerifyPhase("verifying");
    setStep("verify");
    setCommitting(true);
    setInFlight(true);
    const controller = new AbortController();
    submitAbort.current = controller;
    onResumeArchived(hoid, { signal: controller.signal })
      .catch((error: unknown) => {
        if (run !== runId.current) {
          if (!(error instanceof StepUpCancelledError)) showArchiveError();
          return;
        }
        setCommitting(false);
        if (error instanceof StepUpCancelledError) setStep("confirm");
        else setVerifyPhase("error");
      })
      .finally(() => setInFlight(false));
  }, [hoid, resume, onResumeArchived, showArchiveError]);

  const cancelSubmit = () => {
    submitAbort.current?.abort();
    submitAbort.current = null;
    runId.current += 1;
  };
  const leaveVerify = () => {
    cancelSubmit();
    setCommitting(false);
    setStep("confirm");
  };

  const requestClose = () => {
    if (discardOpen) {
      setDiscardOpen(false);
      return;
    }
    if (step === "verify") {
      setDiscardOpen(true);
      return;
    }
    onClose();
  };

  useBodyScrollLock(isMobile && hoid !== null);

  // The mobile shell is a bare positioned `<div>`, not a native `<dialog>`,
  // so none of this is automatic: opening it doesn't move focus off whatever
  // triggered it (the still-mounted, still-focusable detail sheet behind it),
  // and closing it doesn't give focus back. Desktop is untouched here — it
  // goes through `ResponsiveModal`, which has this same gap but is out of
  // scope for this fix.
  useEffect(() => {
    if (!isMobile) return;
    if (hoid) {
      previouslyFocusedRef.current =
        document.activeElement as HTMLElement | null;
      dialogRef.current?.focus();
    } else if (previouslyFocusedRef.current) {
      previouslyFocusedRef.current.focus();
      previouslyFocusedRef.current = null;
    }
  }, [hoid, isMobile]);

  const handleDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      requestClose();
      return;
    }
    if (e.key !== "Tab") return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ??
        [],
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  if (!hoid) return null;

  const handleArchive = async () => {
    setCommitting(true);
    try {
      await onArchived(hoid);
      // On success the parent closes the modal (clears `hoid`); no local
      // state to reset here.
    } catch (error) {
      setCommitting(false);
      setStep("confirm");
      if (error instanceof StepUpCancelledError) return;
      showErrorToast({
        title: t("subIBReport_archiveError", "Couldn't close rebate program"),
        description: t(
          "subIBReport_archiveErrorDescription",
          "Please try again.",
        ),
      });
    }
  };

  const handleContinue = async () => {
    const controller = new AbortController();
    submitAbort.current = controller;
    const run = ++runId.current;
    let redirecting = false;
    setCommitting(true);
    setInFlight(true);
    try {
      await onArchived(hoid, {
        signal: controller.signal,
        onRedirect: () => {
          if (run !== runId.current) return;
          redirecting = true;
          setVerifyPhase("redirect");
          setStep("verify");
        },
      });
    } catch (error) {
      if (run !== runId.current) {
        if (!(error instanceof StepUpCancelledError)) showArchiveError();
        return;
      }
      setCommitting(false);
      if (error instanceof StepUpCancelledError) setStep("confirm");
      else if (redirecting) setVerifyPhase("error");
      else showArchiveError();
    } finally {
      setInFlight(false);
    }
  };

  const partnerNames = hoid.partners.map((p) => p.name);
  // A shared program has 2+ partners, so the notice addresses several Sub IBs
  // even though only one program is being archived (canvas: `subsPlural`).
  const subsPlural = partnerNames.length > 1;

  const effects = [
    subsPlural
      ? t(
          "subIBReport_archiveEffectNotifiedPlural",
          "Your Sub IBs will be notified that these programs are being closed.",
        )
      : t(
          "subIBReport_archiveEffectNotified",
          "Your Sub IB will be notified that this program is being closed.",
        ),
    t(
      "subIBReport_archiveEffectLink",
      "The referral link stays active. New clients can still be onboarded, but you'll receive 100% of their rebates.",
    ),
    subsPlural
      ? t(
          "subIBReport_archiveEffectExistingPlural",
          "You and your Sub IBs continue earning rebates from existing clients in these programs.",
        )
      : t(
          "subIBReport_archiveEffectExisting",
          "You and your Sub IB continue earning rebates from existing clients in this program.",
        ),
    t("subIBReport_archiveEffectFinal", "This action cannot be undone."),
  ];

  // `Typo` has a closed prop set (no `id`), so this can't be wired up via
  // `aria-labelledby` pointing at the rendered heading — same constraint
  // `DiscardConfirmOverlay` hit, same fix: `aria-label` with the same string.
  const title = t(
    "subIBReport_archiveConfirmTitle",
    "Close this rebate program?",
  );

  const totalSteps = 2;
  const currentStepNumber = !showStepUp
    ? 0
    : step === "verify"
      ? verifyPhase === "error"
        ? 0
        : 2
      : 1;
  const isMobileVerify = isMobile && step === "verify";
  const stepLabel =
    step === "confirm" && currentStepNumber > 0
      ? t("subIBReport_stepOf", "STEP {{current}} OF {{total}}", {
          current: currentStepNumber,
          total: totalSteps,
        })
      : undefined;

  const content = (
    <div
      className={clsx(
        "flex min-h-0 flex-1 flex-col",
        isMobileVerify ? "gap-2" : "gap-8",
      )}
    >
      <div className="flex items-center gap-10">
        {currentStepNumber > 0 ? (
          <div
            role="progressbar"
            aria-valuenow={currentStepNumber}
            aria-valuemin={1}
            aria-valuemax={totalSteps}
            aria-label={title}
            className="flex flex-1 gap-1"
          >
            {Array.from({ length: totalSteps }, (_, i) => (
              <span
                key={i}
                className={`h-2 flex-1 rounded-full ${
                  i < currentStepNumber
                    ? "bg-surface-brand-default"
                    : "bg-surface-tonal-bold"
                }`}
              />
            ))}
          </div>
        ) : (
          <span className="flex-1" />
        )}
        <button
          type="button"
          aria-label={t("common_close", "Close")}
          data-testid="archive_program_modal_close_button"
          onClick={requestClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
        >
          <CrossIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {step === "confirm" && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            {stepLabel && (
              <Typo
                element="span"
                type="labelXS"
                textColor="text-text-tertiary"
                className="uppercase tracking-[0.04em]"
              >
                {stepLabel}
              </Typo>
            )}
            <Typo element="h2" type={isMobile ? "headingLG" : "headingXL"}>
              {title}
            </Typo>
          </div>

          <div className="flex flex-col gap-6">
            {/* Program recap — HOID · split, plus who it is shared with. */}
            <div className="flex flex-col gap-1 rounded-lg bg-surface-tonal-default p-4">
              <span className="flex flex-wrap items-center gap-2">
                <Typo element="span" type="labelSM">
                  {hoid.rebateProgramLabel}
                </Typo>
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-tertiary"
                  aria-hidden="true"
                >
                  ·
                </Typo>
                <Typo element="span" type="labelSM">
                  {splitLabel(hoid)}
                </Typo>
              </span>
              {subsPlural && (
                <Typo
                  element="span"
                  type="bodyXS"
                  textColor="text-text-secondary"
                >
                  {t("subIBReport_archiveSharedWith", "Shared with {{names}}", {
                    names: partnerNames.join(", "),
                  })}
                </Typo>
              )}
            </div>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              {effects.map((effect) => (
                <Typo
                  key={effect}
                  element="li"
                  type="bodySM"
                  textColor="text-text-primary"
                >
                  {effect}
                </Typo>
              ))}
            </ul>
          </div>
        </div>
      )}

      {step === "confirm" && (
        <WizardFooter isMobile={isMobile}>
          <ButtonV2
            variant="tonal"
            size="md"
            onClick={onClose}
            disabled={!showStepUp && committing}
            data-testid="archive_program_modal_cancel_button"
          >
            {t("common_cancel", "Cancel")}
          </ButtonV2>
          <ButtonV2
            variant={showStepUp ? "brand" : "negative"}
            size="md"
            onClick={showStepUp ? handleContinue : handleArchive}
            disabled={showStepUp ? inFlight : committing}
            isLoading={showStepUp ? inFlight : committing}
            data-testid="archive_program_modal_confirm_button"
          >
            {showStepUp
              ? t("common_continue", "Continue")
              : t("subIBReport_archiveProgram", "Close rebate program")}
          </ButtonV2>
        </WizardFooter>
      )}

      {step === "verify" && (
        <ArchiveVerifyPhase
          phase={verifyPhase}
          onBack={leaveVerify}
          onClose={() => setDiscardOpen(true)}
          isMobile={isMobile}
        />
      )}
    </div>
  );

  const discardOverlay = discardOpen && (
    <DiscardConfirmOverlay
      onStay={() => setDiscardOpen(false)}
      onDiscard={() => {
        setDiscardOpen(false);
        cancelSubmit();
        onClose();
      }}
      isMobile={isMobile}
    />
  );

  if (isMobile) {
    return createPortal(
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}
        className="fixed inset-0 z-50 flex flex-col bg-surface-primary outline-none"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
          {content}
        </div>
        {discardOverlay}
      </div>,
      document.body,
    );
  }

  return (
    <ResponsiveModal
      isOpen
      onClose={requestClose}
      isMobile={false}
      headerClassName="!hidden"
    >
      {content}
      {discardOverlay}
    </ResponsiveModal>
  );
};

type ArchiveVerifyPhaseProps = {
  phase: VerifyPhase;
  onBack: () => void;
  onClose: () => void;
  isMobile: boolean;
};

function ArchiveVerifyPhase({
  phase,
  onBack,
  onClose,
  isMobile,
}: ArchiveVerifyPhaseProps) {
  const translations = useStepUpFlowTranslations();

  return (
    <>
      {phase === "redirect" && <StepUpRedirect isMobile={isMobile} />}
      {phase === "verifying" && (
        <StepUpVerifying isMobile={isMobile} action="archive" />
      )}
      {phase === "error" && <StepUpError isMobile={isMobile} />}

      <WizardFooter isMobile={isMobile}>
        {phase === "error" ? (
          <>
            <ButtonV2
              variant="tonal"
              size="md"
              onClick={onClose}
              data-testid="archive_program_modal_verify_close_button"
            >
              {translations.close}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="md"
              onClick={onBack}
              data-testid="archive_program_modal_verify_retry_button"
            >
              {translations.retry}
            </ButtonV2>
          </>
        ) : (
          <ButtonV2
            variant="tonal"
            size="md"
            onClick={onBack}
            data-testid="archive_program_modal_verify_back_button"
          >
            {translations.back}
          </ButtonV2>
        )}
      </WizardFooter>
    </>
  );
}
